/**
 * Snake_case wallet helpers.
 * Matches BFF/DB shape — no casing transforms.
 * Converts UI updates to snake_case for tRPC wallet.update.
 */

import type { ChecklistItemSnake } from "../types";

/** Map UI (camelCase) keys to BFF (snake_case) for wallet updates */
const WALLET_UPDATE_KEY_MAP: Record<string, string> = {
	integrationStatus: "integration_status",
	onboardingStatus: "onboarding_status",
	isWhitelisted: "is_whitelisted",
	whitelistEnvironments: "whitelist_environments",
	kycFormCompleted: "kyc_form_completed",
	partnerTasks: "partner_tasks",
	customerSuccess: "customer_success",
	marketingGrant: "marketing_grant",
	feeStructures: "fee_structures",
	assignedUsers: "assigned_users",
};

function camelToSnake(key: string): string {
	return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Convert wallet update keys from UI (camelCase or mixed) to BFF snake_case.
 * Nested objects (kyc_form_completed, fee_structures, etc.) keep snake_case for known fields.
 */
export function walletUpdatesToSnake(
	updates: Record<string, unknown>,
): Record<string, unknown> {
	const result: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(updates)) {
		if (value === undefined) continue;
		const snakeKey =
			WALLET_UPDATE_KEY_MAP[key] ??
			(key.includes("_") ? key : camelToSnake(key));
		// kyc_form_completed nested: ensure completedAt→completed_at, completedBy→completed_by
		if (
			snakeKey === "kyc_form_completed" &&
			value &&
			typeof value === "object"
		) {
			const v = value as Record<string, unknown>;
			const mapped: Record<string, unknown> = {};
			for (const [k, val] of Object.entries(v)) {
				if (val === undefined) continue;
				if (k === "completedAt") mapped.completed_at = val;
				else if (k === "completedBy") mapped.completed_by = val;
				else if (k === "kybFormId") mapped.kyb_form_id = val;
				else if (k === "kybProgress") mapped.kyb_progress = val;
				else if (
					!["completedAt", "completedBy", "kybFormId", "kybProgress"].includes(
						k,
					)
				)
					mapped[k] = val;
			}
			result[snakeKey] = mapped;
		} else {
			result[snakeKey] = value;
		}
	}
	return result;
}

/** Checklist slot keys as returned by BFF (snake_case) */
export const CHECKLIST_KEYS = [
	"salesforce_opportunity_created",
	"nda_signed",
	"consent_form_signed",
	"kyc_form_completed",
	"contract_signed",
	"salesforce_case_created",
	"slack_channel_created",
	"integration_completed",
	"partner_activated",
	"fees_pricing_contacted",
	"testing_completed",
	"whitelisted_in_production",
	"go_live_verification",
	"marketing_follow_up",
] as const;

export type ChecklistKey = (typeof CHECKLIST_KEYS)[number];

/** Human-readable labels for checklist slots */
export const CHECKLIST_LABELS: Record<ChecklistKey, string> = {
	salesforce_opportunity_created: "Create Salesforce Opportunity",
	nda_signed: "NDA Signed",
	consent_form_signed: "Consent Form Signed",
	kyc_form_completed: "KYB Completed",
	contract_signed: "Contract Signed",
	salesforce_case_created: "Create Salesforce Case",
	slack_channel_created: "Slack Channel Created",
	integration_completed: "Integration Completed",
	partner_activated: "Partner Activated",
	fees_pricing_contacted: "Fees Pricing Contacted",
	testing_completed: "Testing Completed",
	whitelisted_in_production: "Whitelisted in Production",
	go_live_verification: "Go Live Verification",
	marketing_follow_up: "Marketing Follow-up",
};

/** Get checklist item by key from snake_case wallet */
export function getChecklistItem(
	wallet: Record<string, unknown>,
	key: ChecklistKey,
): ChecklistItemSnake | undefined {
	const v = wallet[key];
	return v && typeof v === "object" ? (v as ChecklistItemSnake) : undefined;
}

/** Convert ChecklistItemSnake to ChecklistItem for components that need id/label */
export function toChecklistItem(
	key: ChecklistKey,
	item: ChecklistItemSnake | null | undefined,
): import("../types").ChecklistItem {
	const label = CHECKLIST_LABELS[key];
	return {
		id: key.replace(/_/g, "-"),
		label,
		completed: item?.completed ?? false,
		completedAt: item?.completed_at,
		completedBy: item?.completed_by,
		documentUrl: item?.document_url,
		documentName: item?.document_name,
		uploadedAt: item?.uploaded_at,
		kybFormId: item?.kyb_form_id,
		kybProgress: item?.kyb_progress,
	};
}
